import { NO_HANDLE } from "odyssey-shared";

/**
 * Whom an action names (P2.19-T8; ADR-114 decisions 4.1 and 4.2): the combat target the player holds, when it is a
 * player's character or a creature and the page has a name for it. The page only offers the targeted form; the map
 * server composes the sentence from its own table and its own names, and refuses a target it does not hold.
 */

/**
 * The held target as the wheel reads it from the target frame: its handle, its kind and the first name shown.
 */
export interface HeldTarget {
  readonly handle: number;
  readonly kind: "character" | "creature" | "dummy";
  readonly first: string;
}

/**
 * The target an action would name: its handle and its name.
 */
export interface NamedTarget {
  readonly handle: number;
  readonly name: string;
}

/**
 * The target an action names, from the target held.
 *
 * @param target The held target, or null for none.
 * @returns The handle and the name, or null when nothing is held, the target is a training dummy, which has no name,
 *   or the page shows no name for it.
 * @example
 * namedTarget({ handle: 7, kind: "character", first: "Mira" });
 * // { handle: 7, name: "Mira" }
 */
export function namedTarget(target: HeldTarget | null): NamedTarget | null {
  if (target === null || target.kind === "dummy" || target.first === "") {
    return null;
  }
  return { handle: target.handle, name: target.first };
}

/**
 * The handle the wheel sends with an action.
 *
 * @param target The held target, or null for none.
 * @returns The target's handle, or `NO_HANDLE` when the action names no one.
 * @example
 * targetHandle(null);
 * // 65535
 */
export function targetHandle(target: HeldTarget | null): number {
  return namedTarget(target)?.handle ?? NO_HANDLE;
}
